import { Link } from 'react-router-dom';
import { cn, getNameInitials } from '@/lib/utils';

export interface ConsoleIdentityProps {
  /** Logged-in user's full name */
  name: string;
  /** Logged-in user's role (e.g. Administrator) */
  role: string;
  /** Their own profile page. When set the block links there; otherwise it is plain text. */
  to?: string;
}

/**
 * Top Header Identity Block: Shows logged in user avatar, full name, and role.
 */
export function ConsoleIdentity({ name, role, to }: ConsoleIdentityProps) {
  const initials = getNameInitials(name || 'Admin');

  const content = (
    <>
      {/* User Avatar Circle */}
      <span
        aria-hidden
        className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-[14px] font-black text-white shadow-md shadow-blue-500/20 ring-2 ring-blue-500/20"
      >
        {initials}
      </span>

      {/* Logged in User Name & Role */}
      <div className="hidden min-w-0 leading-tight sm:block">
        <span className="block truncate text-[14px] font-extrabold text-ink tracking-tight">
          {name || 'System Admin'}
        </span>
        <span className="block truncate text-[11px] pt-1 font-semibold text-blue-600 dark:text-blue-400">
          {role || 'Administrator'}
        </span>
      </div>
    </>
  );

  if (!to) return <div className="flex items-center gap-3">{content}</div>;

  return (
    <Link
      to={to}
      aria-label={`${name || 'System Admin'} — my profile`}
      className={cn(
        'flex items-center gap-3 rounded-xl p-1 -m-1 transition-colors hover:bg-surface-2',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
      )}
    >
      {content}
    </Link>
  );
}
